import {
  setIsEmailMandatoryForAgencyCode,
  setAgentPreferences,
  setBucketGroup,
  setAvailableRoles,
  setAgentCorporateNumbers
} from '@cp/src/reducers/TeamLeadSlice';
import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import { Dispatch } from '@reduxjs/toolkit';
import { ENTITY_TYPE, VALIDATION_MESSAGE, VERIFICATION_STATUS } from '../constants/constants';
import { IHandleCoordinatesVerificationProps, IHandleVerificationProps } from '../interfaces/types';

export const getManagers = async (refId: string) => {
  try {
    const url = getApiUrl(ApiKeys.GET_MANAGER_LIST, { refID: refId });
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    logError(error, 'Error inside getManagers');
  }
};

export const getEditManagers = async (refId: string) => {
  try {
    const url = getApiUrl(ApiKeys.GET_EDIT_MANAGER_LIST, { refID: refId });
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error) {
    logError(error, 'Error inside getManagers');
  }
};

export const getIsEmailMandatoryForAgencyCode = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_IS_EMAIL_MANDATORY);
  axiosInstance
    .get(url)
    .then(response => {
      dispatch(setIsEmailMandatoryForAgencyCode(response.data));
    })
    .catch(error => {
      logError('Error inside getIsEmailMandatoryForAgencyCode', error);
    });
};

export const getAgentPreferences = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_AGENT_PREFERENCES);
  return axiosInstance
    .get(url)
    .then(response => {
      dispatch(setAgentPreferences(response.data));
    })
    .catch(error => {
      logError('Error inside getAgentPreferences', error);
    });
};

export const getBucketGroup = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_BUCKET_GROUPS);
  return axiosInstance
    .get(url)
    .then(response => {
      dispatch(setBucketGroup(response.data));
    })
    .catch(error => {
      logError('Error inside getBucketList', error);
    });
};

export const getAvailableRoles = () => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_AVAILABLE_ROLES);
  return axiosInstance
    .get(url)
    .then(response => {
      dispatch(setAvailableRoles(response.data));
    })
    .catch(error => {
      logError('Error inside getAvailableRoles', error);
    });
};

export const getPincodeAndCity = async (latitude: string, longitude: string) => {
  const url = getApiUrl(ApiKeys.GET_CITY_AND_PINCODE, {}, { latitude, longitude });
  return axiosInstance
    .get(url)
    .then(response => {
      return response;
    })
    .catch(error => {
      logError('Failed to fetch City and Pincode', error);
    });
};

export const isValidString = (str: string | undefined): boolean => {
  if (!str) return false;
  return str?.toString()?.length > 0;
};

export function isValidDecimalNumber(str: string) {
  const decimalRegex = /^\d+(\.\d+)?$/;
  return decimalRegex.test(str);
}

export const handleVerification = async (props: IHandleVerificationProps) => {
  const {
    getValues,
    setLocationVerificationStatus,
    setShowLocationVerificationToast,
    setShowCityPincode,
    setReverseGeocodedAgentCity,
    setReverseGeocodedAgentPincode,
    setMisMatchedEntity,
    entity
  } = props;
  const enteredLatitude = getValues('nextMonthLatitude');
  const enteredLongitude = getValues('nextMonthLongitude');
  if (!isValidDecimalNumber(enteredLatitude) || !isValidDecimalNumber(enteredLongitude)) {
    toast.error(VALIDATION_MESSAGE.LATITUDE_LONGITUDE_MESSAGE);
    return;
  }
  setLocationVerificationStatus(VERIFICATION_STATUS.IN_PROGRESS);
  setShowLocationVerificationToast(false);
  setShowCityPincode(false);
  getPincodeAndCity(getValues('nextMonthLatitude'), getValues('nextMonthLongitude'))
    .then(response => {
      const city = response?.data?.city || '';
      const pincode = response?.data?.postalCode || '';
      const isCityEntity = entity === ENTITY_TYPE.CITY;
      const entityToVerify = getValues(isCityEntity ? 'agentCity' : 'nextMonthAgentPincode');
      setShowCityPincode(true);
      setReverseGeocodedAgentCity(city);
      setReverseGeocodedAgentPincode(pincode);
      const entityToVerifyAgainst = isCityEntity ? city : pincode;
      if (
        entityToVerify &&
        entityToVerify !== '' &&
        entityToVerifyAgainst !== '' &&
        entityToVerify.toLowerCase() !== entityToVerifyAgainst.toLowerCase()
      ) {
        setMisMatchedEntity(true);
      } else {
        setMisMatchedEntity(false);
      }
      setLocationVerificationStatus(VERIFICATION_STATUS.VERIFIED);
    })
    .catch(error => {
      setShowCityPincode(false);
      setLocationVerificationStatus(VERIFICATION_STATUS.UNVERIFIED);
    });
};

export const handleLocationCoordinatesChange = (props: IHandleCoordinatesVerificationProps) => {
  const {
    setIsEntityMismatch,
    setLocationVerificationStatus,
    setShowVerifyButton,
    setShowCityPincode,
    setShowLocationVerificationToast
  } = props;
  setIsEntityMismatch(false);
  setLocationVerificationStatus(VERIFICATION_STATUS.UNVERIFIED);
  setShowVerifyButton(true);
  setShowCityPincode(false);
  setShowLocationVerificationToast(false);
};

export const getAllCorporateNumbersforAgent =
  (agentRefId: string, setIsCorporateNosLoading: (arg0: boolean) => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GET_CORPORATE_NUMBERS, { agentRefId });
    return axiosInstance
      .get(url)
      .then(response => {
        dispatch(setAgentCorporateNumbers(response.data));
        setIsCorporateNosLoading(false);
      })
      .catch(error => {
        logError('Error inside getCorporateNumbers', error);
      });
  };
